import { create } from 'zustand';
import { readLS, writeLS, removeLS } from '@/services/storage/local';
import { setAuthToken, setOnUnauthorized } from '@/services/api/client';
import { closeCable } from '@/services/api/cable';

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/api\/v\d+$/, '');

const callLogoutEndpoint = async (token: string): Promise<void> => {
  try {
    await fetch(`${apiBase}/auth/logout`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (_error) {
    // Local logout remains valid even when the server session is unreachable.
  }
};

export type AuthRole = 'member' | 'visitor' | 'assoc' | 'admin';
export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: AuthRole;
  association_role?: 'owner' | 'admin' | 'staff' | null;
  has_laudo: boolean;
};

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  setSession: (user: AuthUser, token: string) => void;
  logout: () => Promise<void>;
  clear: () => void;
};

const KEY = 'canapyra-auth';
const TOKEN_KEY = 'canapyra-jwt';

type Persisted = { user: AuthUser | null; token: string | null };
const persisted = readLS<Persisted>(KEY, { user: null, token: null });
if (persisted.token) setAuthToken(persisted.token);

export const useAuthStore = create<AuthState>((set, get) => ({
  user: persisted.user,
  token: persisted.token,
  setSession: (user, token) => {
    writeLS(KEY, { user, token });
    writeLS(TOKEN_KEY, token);
    setAuthToken(token);
    set({ user, token });
  },
  logout: async () => {
    const token = get().token;
    if (token) await callLogoutEndpoint(token);
    closeCable();
    get().clear();
  },
  clear: () => {
    removeLS(KEY);
    removeLS(TOKEN_KEY);
    setAuthToken(null);
    set({ user: null, token: null });
  },
}));

setOnUnauthorized(() => {
  closeCable();
  useAuthStore.getState().clear();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
    window.location.assign('/auth/login');
  }
});

export const useCurrentUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => !!s.token);
